import type { JSONSchema7 } from "json-schema";
import { z } from "zod";

/**
 * Zod shape for a single custom field property — constrained subset of JSON Schema 7.
 * The `satisfies` clause ensures every key is a valid JSON Schema 7 keyword;
 * adding a non-standard key here will cause a compile-time error.
 */
const customFieldPropertyShape = {
  type: z.tuple([z.enum(["string", "number", "array"]), z.literal("null")]),
  format: z.string().optional(),
  enum: z.array(z.union([z.string(), z.null()])).optional(),
  items: z
    .object({
      type: z.literal("string"),
      enum: z.array(z.string()).optional(),
    })
    .optional(),
  description: z.string().optional(),
  default: z.union([z.string(), z.number(), z.array(z.string())]).optional(),
  readOnly: z.boolean().optional(),
} satisfies { [K in keyof JSONSchema7]?: z.ZodTypeAny };

/**
 * Schema for a single property in the custom fields JSON Schema — constrained subset of JSON Schema 7.
 * An `items.enum`, when present, must be nonempty and unique, and a `default`
 * must match the property's type and option list.
 */
export const CustomFieldPropertySchema = z
  .object(customFieldPropertyShape)
  .superRefine((property, ctx) => {
    addOptionListIssues(property, ctx);
    addDefaultValueIssues(property, ctx);
  });
export type CustomFieldProperty = z.infer<typeof CustomFieldPropertySchema>;

/**
 * Schema for the JSON Schema stored in settings that defines available custom fields.
 * Every name listed in `required` must have a matching entry in `properties`.
 */
export const CustomFieldsSchemaSchema = z
  .object({
    type: z.literal("object"),
    properties: z.record(z.string(), CustomFieldPropertySchema),
    additionalProperties: z.literal(false),
    propertyOrder: z.array(z.string()).optional(),
    required: z.array(z.string()).optional(),
  })
  .superRefine((schema, ctx) => {
    for (const name of schema.required ?? []) {
      if (name in schema.properties) {
        continue;
      }
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Required field "${name}" is not defined in properties`,
        path: ["required"],
      });
    }
  });
export type CustomFieldsSchema = z.infer<typeof CustomFieldsSchemaSchema>;

/**
 * Adds issues when a property's `items.enum` option list is empty or has duplicates.
 * @param property - The custom field property being validated
 * @param ctx - The zod refinement context collecting issues
 */
function addOptionListIssues(
  property: CustomFieldProperty,
  ctx: z.RefinementCtx,
): void {
  const options = property.items?.enum;
  if (!options) {
    return;
  }
  if (options.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "items.enum must list at least one option",
      path: ["items", "enum"],
    });
    return;
  }
  if (new Set(options).size !== options.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "items.enum values must be unique",
      path: ["items", "enum"],
    });
  }
}

/**
 * Adds issues when a property's `default` does not match its type or option list.
 * @param property - The custom field property being validated
 * @param ctx - The zod refinement context collecting issues
 */
function addDefaultValueIssues(
  property: CustomFieldProperty,
  ctx: z.RefinementCtx,
): void {
  const defaultValue = property.default;
  if (defaultValue === undefined) {
    return;
  }
  const addIssue = (message: string): void => {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path: ["default"],
    });
  };

  if (property.type[0] === "array") {
    if (!Array.isArray(defaultValue)) {
      addIssue("default must be an array of strings for a multi-value field");
      return;
    }
    const options = property.items?.enum;
    if (!options) {
      return;
    }
    const unknownOptions = defaultValue.filter(
      (value) => !options.includes(value),
    );
    if (unknownOptions.length > 0) {
      addIssue(
        `default is not one of the options: ${unknownOptions.join(", ")}`,
      );
    }
    return;
  }

  if (property.type[0] === "number") {
    if (typeof defaultValue !== "number") {
      addIssue("default must be a number");
    }
    return;
  }

  if (typeof defaultValue !== "string") {
    addIssue("default must be a string");
    return;
  }
  const options = property.enum?.filter((value) => value !== null);
  if (options && !options.includes(defaultValue)) {
    addIssue(`default is not one of the options: ${defaultValue}`);
  }
}

/**
 * Returns custom field names that appear more than once in a schema's propertyOrder.
 * @param schema - The custom fields JSON Schema to check
 * @returns Array of duplicate field names (empty if none)
 */
export const getDuplicateCustomFieldNames = (
  schema: CustomFieldsSchema,
): string[] => {
  const names = schema.propertyOrder ?? Object.keys(schema.properties);
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const name of names) {
    if (seen.has(name)) {
      duplicates.add(name);
    }
    seen.add(name);
  }
  return [...duplicates];
};

/**
 * Whether a custom field holds free text (a string with no format, or the textarea format).
 * @param prop - The custom field property to test
 * @returns True when the field is a plain text or textarea field
 */
export const isTextCustomField = (prop: CustomFieldProperty): boolean =>
  prop.type[0] === "string" &&
  (prop.format === undefined || prop.format === "fpTextarea");

/**
 * Resolve the ordered list of custom field names from a custom fields schema.
 * Uses `propertyOrder` if available, otherwise falls back to `Object.keys`.
 * @param schema - The custom fields JSON Schema from quote settings
 * @returns Ordered array of custom field names
 */
export const getCustomFieldNames = (
  schema: CustomFieldsSchema | null | undefined,
): string[] => {
  if (!schema) {
    return [];
  }
  if (schema.propertyOrder && schema.propertyOrder.length > 0) {
    return schema.propertyOrder;
  }
  return Object.keys(schema.properties);
};

/**
 * Resolve the option list for a select or multi-select custom field.
 * Single-selects store options in a top-level `enum`; multi-selects store
 * them in `items.enum`.
 * @param prop - The custom field property definition
 * @returns The non-null option values, or null when the field has no options
 */
export const getCustomFieldOptions = (
  prop: CustomFieldProperty,
): string[] | null => {
  if (prop.enum) {
    return prop.enum.filter((v): v is string => v !== null);
  }
  return prop.items?.enum ?? null;
};

/**
 * Checks whether a custom field property is a multi-select field
 * (array-typed with an `items.enum` option list).
 * @param prop - The custom field property definition
 * @returns True when the property is a multi-select field
 */
export const isMultiSelectField = (prop: CustomFieldProperty): boolean =>
  prop.type[0] === "array" && prop.items?.enum !== undefined;

/**
 * Checks whether a custom field property is an attachments field
 * (array-typed with the `fpAsset` format, holding Forgepoint asset ids).
 * @param prop - The custom field property definition
 * @returns True when the property is an attachments field
 */
export const isAttachmentsField = (prop: CustomFieldProperty): boolean =>
  prop.type[0] === "array" && prop.format === "fpAsset";

/** Schema for custom field values stored on an entity that carries custom fields. */
export const CustomFieldValuesSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.array(z.string()), z.null()]),
);
export type CustomFieldValues = z.infer<typeof CustomFieldValuesSchema>;

/** A single custom field value. */
export type CustomFieldValue = CustomFieldValues[string];

/**
 * Filter on opportunity custom field values: field name to the values it may hold.
 * An entry matches when the stored value is any of its values; entries AND together.
 */
export const CustomFieldFilterSchema = z.record(
  z.string(),
  z.array(z.string()).min(1),
);
export type CustomFieldFilter = z.infer<typeof CustomFieldFilterSchema>;

/**
 * Query-param form of `CustomFieldFilterSchema`: one JSON-encoded string,
 * since option values can contain commas. Invalid JSON is a validation issue.
 */
export const CustomFieldFilterParamSchema = z
  .string()
  .transform((raw, ctx): unknown => {
    try {
      return JSON.parse(raw);
    } catch {
      ctx.addIssue({ code: "custom", message: "customFields must be JSON" });
      return z.NEVER;
    }
  })
  .pipe(CustomFieldFilterSchema);

/**
 * Resolve the names of the custom fields the schema marks as required.
 * @param schema - The custom fields JSON Schema, or null when not configured
 * @returns The required field names, empty when none are declared
 */
export const getRequiredCustomFieldNames = (
  schema: CustomFieldsSchema | null | undefined,
): string[] => schema?.required ?? [];

/**
 * Resolve the names of the custom fields the schema marks as read-only for users.
 * @param schema - The custom fields JSON Schema, or null when not configured
 * @returns The read-only field names, empty when none are declared
 */
export const getReadOnlyCustomFieldNames = (
  schema: CustomFieldsSchema | null | undefined,
): string[] =>
  Object.entries(schema?.properties ?? {})
    .filter(([, property]) => property.readOnly === true)
    .map(([name]) => name);

/**
 * Checks whether a custom field value counts as blank for required enforcement.
 * @param value - The stored custom field value
 * @returns True when the value is undefined, null, an empty string, or an empty array
 */
export const isBlankCustomFieldValue = (
  value: CustomFieldValue | undefined,
): boolean => {
  if (value === undefined || value === null || value === "") {
    return true;
  }
  return Array.isArray(value) && value.length === 0;
};
